/**
 * Site-group list grid — customer-scoped list of site groups (plants).
 *
 * Server-backed via EntityGrid over odl-app/site-groups/. Customer scope is
 * enforced through extraParams.customer_id. Supports bulk deactivate
 * (select-all + explicit ids), bulk reactivate, and bulk transfer to
 * another customer (gated to users who can edit groups and have
 * multi-customer access) — all via the odl-app site-groups bulk endpoints.
 * Staff see a status filter; the toolbar also includes the upload dialog on
 * desktop. Mobile renders each row as a card.
 *
 * Summary stat cards (groups/alarms/maintenance counts) come from the odl-app
 * site-groups/stats/ aggregate, passed the grid's active filter[...] params so
 * the counts honor the current scope/status/health filters. A site held by two
 * groups counts once. Separate from the EntityGrid data fetch.
 *
 * Shared across tiers: the customer tier mounts it as-is; a tier that mounts
 * it under its own route (services) supplies its row destination via
 * `siteGroupPath`, an extended descriptor via `basePath`, and an `extension`
 * bag — `{ params, statsHeader, demoteBaseQuickFilters, hideWriteActions,
 * mobile }` (memoized by the caller): extra list params, replaced/dropped
 * stat cards (null drops them), base quick chips demoted to the More popover,
 * the create/upload affordances hidden, and `mobile.getSections(row)` for the
 * card sections the tier adds on a phone (its descriptor's extra fields have
 * no desktop-column equivalent there).
 *
 * Contains:
 * - SiteGroupGrid     — EntityGrid over odl-app/site-groups/ (default export)
 * - SiteGroupListItem — mobile card
 */

import { Capacitor } from '@capacitor/core';
import CloudIcon from '@mui/icons-material/Cloud';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import FilterBar from '@sophocles/components/FilterBar';
import { buildStatusQuickFilter } from '@sophocles/components/FilterBar/statusQuickFilter';
import UploadGroupDataDialog from '@sophocles/components/UploadGroupDataDialog';
// eslint-disable-next-line no-restricted-imports -- genuine warning, not a destructive confirm
import WarningDialog from '@sophocles/components/WarningDialog';
import { EntityGrid } from '@sophocles/data-grids';
import GridActiveCell from '@sophocles/data-grids/components/cells/GridActiveCell';
import GridNameCell from '@sophocles/data-grids/components/cells/GridNameCell';
import { composeMobileCardSections } from '@sophocles/data-grids/components/mobile/cardSections';
import GridMobileCard from '@sophocles/data-grids/components/mobile/GridMobileCard';
import { toJsonApiParams } from '@sophocles/data-grids/datasource/params';
import { SELECTION_ACTION_SX, resolveSelectionRows } from '@sophocles/data-grids/datasource/selectionRows';
import useColumnVisibilityModel from '@sophocles/data-grids/datasource/useColumnVisibilityModel';
import useGridCardFilters from '@sophocles/data-grids/datasource/useGridCardFilters';
import { useGridSessionKey } from '@sophocles/data-grids/datasource/useGridPreferenceKey';
import useGridRefreshRef from '@sophocles/data-grids/datasource/useGridRefreshRef';
import useGridSelectAll from '@sophocles/data-grids/datasource/useGridSelectAll';
import useStatCardOrderModel from '@sophocles/data-grids/datasource/useStatCardOrderModel';
import useStatCardVisibilityModel from '@sophocles/data-grids/datasource/useStatCardVisibilityModel';
import { omitOwnedFilterControls } from '@sophocles/data-grids/grid/descriptorFilters';
import { RECEIVE_SITE_GROUPS } from '@sophocles/entities/SiteGroup';
import {
  useDeactivateSiteGroupsMutation,
  useReactivateSiteGroupsMutation,
  useTransferSiteGroupsMutation,
} from '@sophocles/features/entities/siteGroups';
import { addToast } from '@sophocles/features/toast';
import usePersistedState from '@sophocles/hooks/usePersistedState';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import useTenantTerms from '@sophocles/hooks/useTenantTerms';
import { siteGroupLandingPath } from '@sophocles/router/paths';
import { genericErrorAction } from '@sophocles/utils';
import CreateSiteGroupDialog from '@sophocles/views/siteGroups/dialogs/CreateSiteGroupDialog';

import BulkDeactivateSiteGroupDialog from './BulkDeactivateSiteGroupDialog';
import TransferSiteGroupsDialog from './TransferSiteGroupsDialog';

/** The page adds no quick chips of its own; the descriptor's are the bar. */
const NO_PAGE_QUICK_FILTERS = Object.freeze([]);

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SiteGroupListItem({ row, to, extraSections }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { meta, chips, actions } = composeMobileCardSections(null, extraSections);

  return (
    <GridMobileCard
      onClick={() => navigate(to)}
      primary={
        <>
          {row.name}
          {row.is_virtual ? (
            <CloudIcon sx={{ ml: 0.5, fontSize: 18, color: 'text.secondary', verticalAlign: 'text-bottom' }} />
          ) : null}
          {row.is_inventory ? (
            <WarehouseIcon
              titleAccess={t('siteGroup.isInventory', 'Inventory Group')}
              sx={{ ml: 0.5, fontSize: 18, color: 'text.secondary', verticalAlign: 'text-bottom' }}
            />
          ) : null}
        </>
      }
      secondary={row.description || null}
      meta={meta}
      chips={chips}
      actions={actions}
      // deactivated is staff-only on the descriptor and redacted from a
      // non-staff row, so the chip renders only when the row carries the flag
      // — never as an "Active" chip derived from a missing value.
      status={row.deactivated === undefined ? null : <GridActiveCell deactivated={row.deactivated} />}
    />
  );
}

// ---------------------------------------------------------------------------
// Main grid component
// ---------------------------------------------------------------------------

export default function SiteGroupGrid({
  customer,
  defaultStatus = 'active',
  persistScope = 'siteGroupGrid',
  basePath = 'site-groups',
  siteGroupPath = null,
  extension = null,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isStaff, permissions } = useUser();
  const { isViewOnly, canEditSiteGroup, isReseller } = permissions;
  // Transfer moves a group (and its sites) to another customer, so it needs
  // both edit rights on groups and access to more than one customer.
  const canTransferSiteGroups = canEditSiteGroup && (isStaff || isReseller);

  // The site-group term ("Group"/"Plant"), resolved CustomerType override →
  // tenant term → i18n default. Names the grid's own subject in the copy this
  // component writes itself — button labels, dialog prose, the row count.
  const siteGroupTerm = useSiteGroupTerm(customer);

  const [statusFilter, setStatusFilter] = usePersistedState(useGridSessionKey(persistScope, 'status'), defaultStatus);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [confirmReactivateOpen, setConfirmReactivateOpen] = useState(false);
  const [reactivateIds, setReactivateIds] = useState([]);
  const [deactivateIds, setDeactivateIds] = useState([]);
  const [deactivateRows, setDeactivateRows] = useState([]);
  const [deactivateSelectAll, setDeactivateSelectAll] = useState(false);
  const [transferIds, setTransferIds] = useState([]);
  const [transferSelectAll, setTransferSelectAll] = useState(false);
  const [transferDialogCount, setTransferDialogCount] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState(null);
  const columnVisibility = useColumnVisibilityModel(persistScope);
  const statCardVisibility = useStatCardVisibilityModel(persistScope);
  const statCardOrder = useStatCardOrderModel(persistScope);
  const tenantTerms = useTenantTerms(customer);
  // The health filter is a stat card's filter param, so it lives in the card
  // model rather than beside it — the card and the Health control below write
  // one value and can never disagree about what is applied.
  const customerId = customer?.id;

  // Filters are scoped to the customer being looked at; a column layout or a
  // card order is a preference and is not. What a filter offers is per customer
  // type — one customer's load types are not the next one's — so carrying a
  // selection across a customer switch would filter by something that customer
  // does not have, and coming back restores what was left applied here.
  const cardFilters = useGridCardFilters(customerId ? `${persistScope}:c${customerId}` : persistScope);
  const isBillboard = customer?.is_billboard ?? true;

  // Every row affordance — name cell, mobile card, trailing action — deep-links
  // to the same destination. The customer tier goes to the group's landing
  // view, which is inventory for a group that holds stock instead of
  // structures; a tier that mounts this grid under its own route supplies its
  // own target.
  const resolveSiteGroupPath = useCallback(
    row => (siteGroupPath ? siteGroupPath(row) : siteGroupLandingPath(row, customerId)),
    [siteGroupPath, customerId],
  );

  // Stable ref for the grid's refresh fn — avoids storing a function in state
  const { onGridReady: handleGridReady, refresh: refreshGrid } = useGridRefreshRef();

  // Sync when the parent changes the default status (e.g. deactivated view)
  useEffect(() => {
    if (defaultStatus !== statusFilter) {
      setStatusFilter(defaultStatus);
    }
  }, [defaultStatus]);

  // extraParams scope the EntityGrid list fetch to this customer + status +
  // the applied card filters, plus any tier extension params (must be memoized
  // by the caller — e.g. the services tier excludes virtual groups). The stat
  // cards read the same params, so their numbers honor the same filters.
  const extraParams = useMemo(() => {
    const params = { customer_id: customerId, ...(extension?.params ?? {}), ...cardFilters.params };
    if (statusFilter === 'deactivated') params.deactivated = true;
    else if (statusFilter !== 'all') params.deactivated = false;
    return params;
  }, [customerId, statusFilter, cardFilters.params, extension?.params]);

  // A tier extension may replace the stat cards (node) or drop them (null);
  // undefined keeps the default health cards.
  const showDefaultStats = extension?.statsHeader === undefined;

  // Filter dict for the select-all bulk actions (transfer, deactivate):
  // the exact JSON:API params (filter[<field>] keys) the grid sends to
  // odl-app/site-groups/ — same shape SiteGrid.jsx builds for its own bulk
  // actions — so select-all targets precisely the currently-filtered set
  // (including customer scope), not every group the user can see.
  const selectAllFilters = useMemo(() => toJsonApiParams(extraParams), [extraParams]);

  // Select-all clears whenever filters change so stale selections don't linger.
  const {
    selectAll: selectAllOption,
    active: selectAllActive,
    onClear: clearSelectAll,
  } = useGridSelectAll(extraParams);

  const [transferSiteGroups] = useTransferSiteGroupsMutation();
  const [deactivateSiteGroups] = useDeactivateSiteGroupsMutation();
  const [reactivateSiteGroupsAction] = useReactivateSiteGroupsMutation();

  const confirmTransfer = useCallback(
    async (targetCustomerId, { shareResolution } = {}) => {
      const result = await transferSiteGroups({
        siteGroupIds: transferIds,
        selectAll: transferSelectAll,
        filters: selectAllFilters,
        targetCustomerId,
        shareResolution,
      });
      if (result.error) {
        dispatch(
          addToast({
            title: t('general.error', 'Error'),
            message: t('siteGroups.transferFailed', 'Failed to transfer {{term}}', { term: siteGroupTerm.plural }),
            level: 'error',
            dismissible: true,
          }),
        );
        return;
      }
      dispatch(
        addToast({
          title: t('general.success', 'Success'),
          message: t('siteGroups.transferSuccess', '{{term}} Transferred', { term: siteGroupTerm.plural }),
          level: 'success',
          dismissible: true,
          autoDismiss: 5,
        }),
      );
      setTransferIds([]);
      setTransferSelectAll(false);
      clearSelectAll();
      refreshGrid();
    },
    [
      transferSiteGroups,
      transferIds,
      transferSelectAll,
      selectAllFilters,
      dispatch,
      t,
      siteGroupTerm,
      clearSelectAll,
      refreshGrid,
    ],
  );

  const reactivateSiteGroups = useCallback(() => {
    reactivateSiteGroupsAction({ siteGroupIds: reactivateIds })
      .unwrap()
      .then(() => {
        dispatch(
          addToast({
            title: t('general.success'),
            message: t('siteGroups.reactivateSuccess', '{{term}} Reactivated', {
              term: reactivateIds.length === 1 ? siteGroupTerm.singular : siteGroupTerm.plural,
            }),
            level: 'success',
            dismissible: true,
            autoDismiss: 5,
          }),
        );
        refreshGrid();
      })
      .catch(error => dispatch(genericErrorAction(error, RECEIVE_SITE_GROUPS)));
  }, [reactivateSiteGroupsAction, dispatch, reactivateIds, t, siteGroupTerm, refreshGrid]);

  const confirmDeactivation = useCallback(
    ({ radio_action, destination_site_group_id }) => {
      deactivateSiteGroups({
        ...(deactivateSelectAll ? { selectAll: true, filters: selectAllFilters } : { siteGroupIds: deactivateIds }),
        radioAction: radio_action,
        destinationSiteGroupId: destination_site_group_id,
      })
        .unwrap()
        .then(() => {
          setDeactivateIds([]);
          setDeactivateRows([]);
          setDeactivateSelectAll(false);
          clearSelectAll();
          refreshGrid();
        });
    },
    [deactivateSiteGroups, deactivateSelectAll, selectAllFilters, deactivateIds, clearSelectAll, refreshGrid],
  );

  // Descriptor column render overrides
  const fieldOverrides = useMemo(
    () => ({
      name: {
        cellClassName: 'cell-pinned',
        renderCell: ({ row, value }) => (
          <GridNameCell
            to={resolveSiteGroupPath(row)}
            primary={value}
            secondary={row.description}
            trailing={
              <>
                {row.is_virtual ? <CloudIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} /> : null}
                {row.is_inventory ? (
                  <WarehouseIcon
                    titleAccess={t('siteGroup.isInventory', 'Inventory Group')}
                    sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }}
                  />
                ) : null}
              </>
            }
          />
        ),
      },
      deactivated: {
        renderCell: ({ value }) => <GridActiveCell deactivated={value} />,
      },
    }),
    [resolveSiteGroupPath],
  );

  // Export overrides: `deactivated` reads as a status on screen, so the file
  // states the same Active/Deactivated words rather than the raw flag's Yes/No.
  // Everything else exports off its descriptor type — description included,
  // which the descriptor marks export:"always" beside name.
  const exportOptions = useMemo(
    () => ({
      fileName: 'site-groups.csv',
      columns: {
        deactivated: {
          valueGetter: row => (row.deactivated ? t('general.deactivated') : t('general.active', 'Active')),
        },
      },
    }),
    [t],
  );

  // The descriptor's trailing "select" action deep-links to the group's sites
  // grid — same destination as the name cell and mobile card. Rendered as a
  // contained button (variant: 'button') rather than the default icon so it
  // reads as the row's primary affordance.
  const actionHandlers = useMemo(
    () => ({
      select: { onClick: row => navigate(resolveSiteGroupPath(row)), variant: 'button' },
    }),
    [navigate, resolveSiteGroupPath],
  );

  // Selection toolbar — resolves positional grid IDs to real backend IDs at
  // click time (infinite scroll uses position-based row keys internally)
  const selectionActions = useCallback(
    (selection, _clearSelection, apiRef) => {
      if (isViewOnly) return null;
      const { selectAll, activeRows, deactivatedRows } = resolveSelectionRows(selection, apiRef);
      const showActiveActions = selectAll || activeRows.length > 0;
      const actionSx = SELECTION_ACTION_SX;

      // Select-all count comes from the server-reported total (via
      // onTotalRowCountChange); MUI's pagination.rowCount is the -1 unknown
      // sentinel in infinite-scroll mode. Null before the first fetch → 0,
      // which the dialog title renders count-less.
      const selectAllCount = Math.max(totalRowCount ?? 0, 0);

      return (
        <>
          {showActiveActions && canTransferSiteGroups && (
            <Button
              size="small"
              startIcon={<SwapHorizIcon />}
              onClick={() => {
                setTransferSelectAll(selectAll);
                setTransferIds(selectAll ? [] : activeRows.map(row => row.id));
                setTransferDialogCount(selectAll ? selectAllCount : activeRows.length);
              }}
              sx={actionSx}
            >
              {t('actions.transfer', 'Transfer')}
            </Button>
          )}
          {showActiveActions && (
            <Button
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => {
                setDeactivateSelectAll(selectAll);
                setDeactivateIds(selectAll ? [] : activeRows.map(row => row.id));
                setDeactivateRows(selectAll ? [] : activeRows);
              }}
              sx={actionSx}
            >
              {t('actions.delete', 'Delete')}
            </Button>
          )}
          {deactivatedRows.length > 0 && (
            <Button
              size="small"
              startIcon={<RestoreIcon />}
              onClick={() => {
                setReactivateIds(deactivatedRows.map(row => row.id));
                setConfirmReactivateOpen(true);
              }}
              sx={actionSx}
            >
              {t('siteGroups.reactivateTerm', 'Reactivate {{term}}', { term: siteGroupTerm.plural })}
            </Button>
          )}
        </>
      );
    },
    [isViewOnly, canTransferSiteGroups, t, siteGroupTerm, totalRowCount],
  );

  // `param` on each own control names the list param it narrows, so a filter
  // the descriptor publishes for the same param is not drawn twice.
  const statusFilterDefs = useMemo(
    () =>
      isStaff
        ? [{ ...buildStatusQuickFilter({ t, value: statusFilter, onChange: setStatusFilter }), param: 'deactivated' }]
        : [],
    [t, isStaff, statusFilter, setStatusFilter],
  );

  // The bar's chips are the questions a reader asks about a group; the page
  // adds none of its own beyond what the descriptor publishes.
  const quickFilters = NO_PAGE_QUICK_FILTERS;

  // More filters — the page's own. Health and load type come from the
  // descriptor, which states every value they accept and how many rows sit
  // behind each; active-or-deactivated is the one control the descriptor
  // cannot state, and it scopes the whole list rather than answering a
  // question about one group, so it reads behind the popover rather than as
  // a chip in the bar.
  const moreFilters = useMemo(() => statusFilterDefs, [statusFilterDefs]);

  const handleResetAll = useCallback(() => {
    setStatusFilter('active');
    cardFilters.clear();
  }, [setStatusFilter, cardFilters]);
  // A tier extension may demote the base quick chips into the More-filters
  // popover (the services tier keeps its bar clear for its own filters).
  const demoteBaseQuickFilters = Boolean(extension?.demoteBaseQuickFilters);
  // Params this page still draws its own control for; a published filter over
  // one of them is dropped rather than doubled.
  const ownFilterParams = useMemo(
    () => [...quickFilters, ...moreFilters].map(filter => filter.param).filter(Boolean),
    [quickFilters, moreFilters],
  );
  // Taking toolbarContent as a function is how a page reads the descriptor's
  // published filters: health and inventory arrive as ready controls, already
  // wired to the card-filter model by param, so a card click and the control
  // over the same param hold one value.
  const toolbarContent = useCallback(
    ({ quickFilters: publishedQuick, moreFilters: publishedMore }) => {
      const ownQuick = [...quickFilters, ...omitOwnedFilterControls(publishedQuick, ownFilterParams)];
      const more = [...moreFilters, ...omitOwnedFilterControls(publishedMore, ownFilterParams)];
      // A tier extension may demote the base chips into the popover to keep
      // the bar clear for its own.
      const quick = demoteBaseQuickFilters ? [] : ownQuick;
      const rest = demoteBaseQuickFilters ? [...ownQuick, ...more] : more;
      if (quick.length === 0 && rest.length === 0) return null;
      return <FilterBar quickFilters={quick} moreFilters={rest} onResetAll={handleResetAll} />;
    },
    [quickFilters, moreFilters, ownFilterParams, demoteBaseQuickFilters, handleResetAll],
  );

  // A tier extension contributes its own card sections per row (the services
  // tier's job progress). It hands back content, never layout — the card
  // decides how sections are laid out, and this grid never learns what the
  // tier's fields mean.
  const extensionMobileSections = extension?.mobile?.getSections;
  const renderMobileCard = useCallback(
    row => (
      <SiteGroupListItem
        row={row}
        to={resolveSiteGroupPath(row)}
        extraSections={extensionMobileSections?.(row) ?? null}
      />
    ),
    [resolveSiteGroupPath, extensionMobileSections],
  );

  // undefined lets EntityGrid build the descriptor's cards; a tier extension
  // replaces them with its own node or drops them with null.
  const statsHeader = showDefaultStats ? undefined : (extension.statsHeader ?? null);

  // A tier extension may drop the write affordances — the services tier
  // picks plants to schedule work in, it doesn't create or upload them.
  const hideWriteActions = Boolean(extension?.hideWriteActions);
  const createAction =
    !isViewOnly && !hideWriteActions
      ? {
          label: t('general.create', 'Create') + ' ' + siteGroupTerm.singular,
          onClick: () => setIsCreateDialogOpen(true),
        }
      : undefined;

  const toolbarActions =
    !hideWriteActions && !isMobile && isStaff && !Capacitor.isNativePlatform() && customerId ? (
      <UploadGroupDataDialog customerId={customerId} />
    ) : null;

  return (
    <>
      <EntityGrid
        basePath={basePath}
        fieldOverrides={fieldOverrides}
        actionHandlers={actionHandlers}
        extraParams={extraParams}
        options={{
          searchPlaceholder: t('siteGroups.searchSiteGroups', 'Search {{term}}', { term: siteGroupTerm.plural }),
          persistKey: persistScope,
          infiniteScroll: true,
          createAction,
          toolbarContent,
          toolbarActions,
          selection: {
            enabled: true,
            actions: selectionActions,
            selectAll: selectAllOption,
          },
          onGridReady: handleGridReady,
          onTotalRowCountChange: setTotalRowCount,
          statsHeader,
          statCards: {
            enabled: Boolean(customerId) && showDefaultStats,
            visibility: statCardVisibility,
            order: statCardOrder,
            filters: cardFilters,
          },
          terms: tenantTerms,
          mobile: { renderCard: renderMobileCard },
          columnVisibility,
          countNoun: siteGroupTerm.plural,
          export: exportOptions,
        }}
      />

      <CreateSiteGroupDialog
        open={isCreateDialogOpen}
        customerId={customerId}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={null}
      />

      <WarningDialog
        open={confirmReactivateOpen}
        title={t('dialogs.areYouSure', 'Are you sure?')}
        body={
          <Box>
            <Typography variant="body2">
              {t('siteGroups.reactivateTerm', 'Reactivate {{term}}', { term: siteGroupTerm.plural })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {reactivateIds.length}{' '}
              {reactivateIds.length === 1 ? t('general.item', 'item') : t('general.items', 'items')}
            </Typography>
          </Box>
        }
        onCancel={() => setConfirmReactivateOpen(false)}
        onConfirm={() => {
          setConfirmReactivateOpen(false);
          reactivateSiteGroups();
        }}
      />

      <BulkDeactivateSiteGroupDialog
        open={deactivateIds.length > 0 || deactivateSelectAll}
        onClose={() => {
          setDeactivateIds([]);
          setDeactivateRows([]);
          setDeactivateSelectAll(false);
        }}
        onConfirm={confirmDeactivation}
        siteGroupIds={deactivateIds}
        siteGroupRows={deactivateRows}
        selectAll={deactivateSelectAll}
        filters={selectAllFilters}
        customerId={customerId}
        isBillboard={isBillboard}
      />

      <TransferSiteGroupsDialog
        open={transferIds.length > 0 || transferSelectAll}
        onClose={() => {
          setTransferIds([]);
          setTransferSelectAll(false);
        }}
        onConfirm={confirmTransfer}
        count={transferDialogCount}
        currentCustomerId={customerId}
        siteGroupIds={transferIds}
        selectAll={transferSelectAll}
        filters={selectAllFilters}
      />
    </>
  );
}
